import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
  importClients,
} from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Pagination,
} from "@/components/ui";
import AppLoading from "@/components/AppLoading";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { PlusCircleIcon, DownloadIcon, PencilIcon, CopyIcon, ClockIcon } from "@/components/icons";
import UploadFileIcon from "@mui/icons-material/UploadFile";

/** Maps backend validation messages to user-friendly Hebrew when isRTL; preserves invalid value in message */
const getValidationMessage = (message, isRTL) => {
  if (!message) return message;
  if (!isRTL) return message;
  const fullMap = {
    "Name is required. Please enter the client's name.": "שדה השם חובה. נא להזין את שם הלקוח.",
    "Phone is required. Please enter a phone number.": "שדה הטלפון חובה. נא להזין מספר טלפון.",
  };
  if (fullMap[message]) return fullMap[message];
  const prefixSuffixMap = [
    [
      "Name contains invalid characters. Invalid value: ",
      "בשם יש תווים לא חוקיים. ערך לא תקין: ",
      ". Only letters, numbers, spaces, hyphen (-) and apostrophe (') are allowed.",
      ". רק אותיות, ספרות, רווחים, מקף (-) וגרש (') מותרים.",
    ],
    [
      "Phone number is invalid. Invalid value: ",
      "מספר הטלפון אינו תקין. ערך לא תקין: ",
      ". Must be 9 to 15 digits only (optional leading +).",
      ". חייב להכיל 9–15 ספרות בלבד (ניתן להתחיל ב-+).",
    ],
    [
      "Email address is invalid. Invalid value: ",
      "כתובת האימייל אינה תקינה. ערך לא תקין: ",
      ". Use format: name@example.com.",
      ". נא להשתמש בפורמט: name@example.com.",
    ],
    [
      "A client with this phone number already exists. Duplicate value: ",
      "לקוח עם מספר טלפון זה כבר קיים. ערך כפול: ",
      ". Phone numbers must be unique.",
      ". מספר הטלפון חייב להיות ייחודי.",
    ],
  ];
  for (const [enPrefix, hePrefix, enSuffix, heSuffix] of prefixSuffixMap) {
    if (message.startsWith(enPrefix) && message.endsWith(enSuffix)) {
      const valuePart = message.slice(enPrefix.length, message.length - enSuffix.length);
      return hePrefix + valuePart + heSuffix;
    }
  }
  return message;
};

const escapeCsv = (v) => {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const guessColumnMapping = (fileColumns) => {
  const lower = (s) => (s || "").toLowerCase().trim();
  const mapping = { name: "", phone: "", email: "", company: "", role: "", notes: "", tags: "" };
  const nameLike = ["name", "שם", "client", "לקוח", "fullname", "full name"];
  const phoneLike = ["phone", "טלפון", "mobile", "נייד", "tel", "telephone", "number"];
  const emailLike = ["email", "אימייל", "mail", "e-mail"];
  const companyLike = ["company", "חברה", "organization", "ארגון", "firm"];
  const roleLike = ["role", "תפקיד", "title", "position", "job"];
  const notesLike = ["notes", "הערות", "note", "remarks", "comments"];
  const tagsLike = ["tags", "תגיות", "tag", "labels", "categories"];
  fileColumns.forEach((col) => {
    const c = lower(col);
    if (nameLike.some((n) => c.includes(n)) && !mapping.name) mapping.name = col;
    if (phoneLike.some((p) => c.includes(p)) && !mapping.phone) mapping.phone = col;
    if (emailLike.some((e) => c.includes(e)) && !mapping.email) mapping.email = col;
    if (companyLike.some((x) => c.includes(x)) && !mapping.company) mapping.company = col;
    if (roleLike.some((x) => c.includes(x)) && !mapping.role) mapping.role = col;
    if (notesLike.some((x) => c.includes(x)) && !mapping.notes) mapping.notes = col;
    if (tagsLike.some((x) => c.includes(x)) && !mapping.tags) mapping.tags = col;
  });
  if (!mapping.name && fileColumns.length > 0) mapping.name = fileColumns[0];
  if (!mapping.phone && fileColumns.length > 1) mapping.phone = fileColumns[1];
  if (!mapping.email && fileColumns.length > 2) mapping.email = fileColumns[2];
  return mapping;
};

const CompanyClients = () => {
  const navigate = useNavigate();
  const { isRTL } = useApp();
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";
  const dateFieldSx = {
    width: 150,
    ...(isDarkMode && {
      "& input[type='date']::-webkit-calendar-picker-indicator": {
        filter: "invert(1)",
        opacity: 0.85,
      },
    }),
  };
  const fileInputRef = useRef(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "", tags: "", company: "", role: "" });
  const [addError, setAddError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(25);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hasEmailFilter, setHasEmailFilter] = useState("");
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [updatedAtFrom, setUpdatedAtFrom] = useState("");
  const [updatedAtTo, setUpdatedAtTo] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [fileColumns, setFileColumns] = useState([]);
  const [uploadedRows, setUploadedRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({ name: "", phone: "", email: "", company: "", role: "", notes: "", tags: "" });
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importPopperAnchor, setImportPopperAnchor] = useState(null);
  const importPopperCloseTimerRef = useRef(null);

  const handleImportPopperOpen = (e) => {
    if (importPopperCloseTimerRef.current) {
      clearTimeout(importPopperCloseTimerRef.current);
      importPopperCloseTimerRef.current = null;
    }
    setImportPopperAnchor(e.currentTarget);
  };

  const handleImportPopperClose = () => {
    importPopperCloseTimerRef.current = setTimeout(() => {
      setImportPopperAnchor(null);
      importPopperCloseTimerRef.current = null;
    }, 150);
  };

  const cancelImportPopperClose = () => {
    if (importPopperCloseTimerRef.current) {
      clearTimeout(importPopperCloseTimerRef.current);
      importPopperCloseTimerRef.current = null;
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === clients.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(clients.map((c) => c._id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const msg = isRTL
      ? `למחוק ${selectedIds.size} לקוחות? פעולה זו אינה הפיכה.`
      : `Delete ${selectedIds.size} clients? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBulkLoading(true);
    try {
      await Promise.all([...selectedIds].map((id) => deleteClient(id)));
      setSelectedIds(new Set());
      await loadClients();
    } catch (err) {
      console.error("Bulk delete failed:", err);
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [search, page, sortBy, sortOrder, hasEmailFilter, createdAtFrom, createdAtTo, updatedAtFrom, updatedAtTo]);

  const clearFilters = () => {
    setSearch("");
    setHasEmailFilter("");
    setCreatedAtFrom("");
    setCreatedAtTo("");
    setUpdatedAtFrom("");
    setUpdatedAtTo("");
    setPage(1);
    navigate("/company/clients", { replace: true });
  };

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await getClients({
        search: search || undefined,
        page,
        limit,
        sortBy,
        sortOrder,
        hasEmail: hasEmailFilter === "yes" ? true : hasEmailFilter === "no" ? false : undefined,
        createdAtFrom: createdAtFrom || undefined,
        createdAtTo: createdAtTo || undefined,
        updatedAtFrom: updatedAtFrom || undefined,
        updatedAtTo: updatedAtTo || undefined,
      });
      setClients(data?.clients || []);
      setTotal(data?.total ?? 0);
      setTotalPages(data?.totalPages ?? 1);
      setSelectedIds(new Set());
    } catch (err) {
      console.error("Failed to load clients:", err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else setSortBy(field);
    setPage(1);
  };

  const parseFile = async (file) => {
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".csv")) {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (res) => {
            const rows = (res.data || []).filter((r) => Object.keys(r).length > 0);
            const cols = res.meta?.fields || (rows[0] ? Object.keys(rows[0]) : []);
            resolve({ rows, columns: cols });
          },
          error: (err) => reject(err),
        });
      });
    }
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array", raw: false });
      const firstSheet = wb.SheetNames[0];
      const sheet = wb.Sheets[firstSheet];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      const headers = (data[0] || []).map((h) => String(h).trim() || "Column");
      const rows = data.slice(1).map((row) => {
        const o = {};
        headers.forEach((h, i) => {
          o[h] = row[i] != null ? String(row[i]).trim() : "";
        });
        return o;
      });
      return { rows, columns: headers };
    }
    throw new Error(isRTL ? "קובץ לא נתמך. השתמש ב-CSV או XLSX." : "Unsupported file. Use CSV or XLSX.");
  };

  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const { rows, columns } = await parseFile(file);
      if (rows.length === 0 || columns.length === 0) {
        alert(isRTL ? "לא נמצאו שורות או עמודות בקובץ." : "No rows or columns found in file.");
        return;
      }
      setFileColumns(columns);
      setUploadedRows(rows);
      setColumnMapping(guessColumnMapping(columns));
      setImportResult(null);
      setShowImportModal(true);
    } catch (err) {
      console.error("Parse error:", err);
      alert(err.message || (isRTL ? "שגיאה בקריאת הקובץ." : "Error reading file."));
    }
  };

  const getMappedRows = () => {
    return uploadedRows.map((row) => {
      const tagsRaw = columnMapping.tags && row[columnMapping.tags] != null ? String(row[columnMapping.tags]) : "";
      const tagsArr = tagsRaw ? tagsRaw.split(/[,;]/).map((t) => t.trim()).filter(Boolean) : [];
      return {
        name: (row[columnMapping.name] != null ? String(row[columnMapping.name]) : "").trim(),
        phone: (row[columnMapping.phone] != null ? String(row[columnMapping.phone]) : "").replace(/[\s-]/g, ""),
        email: (row[columnMapping.email] != null ? String(row[columnMapping.email]) : "").trim().toLowerCase(),
        company: (columnMapping.company && row[columnMapping.company] != null ? String(row[columnMapping.company]) : "").trim(),
        role: (columnMapping.role && row[columnMapping.role] != null ? String(row[columnMapping.role]) : "").trim(),
        notes: (columnMapping.notes && row[columnMapping.notes] != null ? String(row[columnMapping.notes]) : "").trim(),
        tags: tagsArr,
      };
    });
  };

  const canImport = () => {
    const mapped = getMappedRows();
    return mapped.length > 0 && mapped.every((r) => r.name && r.phone);
  };

  const handleImportSubmit = async () => {
    if (!canImport()) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const clientsToImport = getMappedRows();
      const result = await importClients(clientsToImport);
      setImportResult(result);
      await loadClients();
      if (result?.failed === 0) {
        setShowImportModal(false);
        setUploadedRows([]);
        setFileColumns([]);
        setColumnMapping({ name: "", phone: "", email: "", company: "", role: "", notes: "", tags: "" });
      }
    } catch (err) {
      console.error("Import failed:", err);
      setImportResult({
        imported: 0,
        failed: uploadedRows.length,
        errors: [{ message: err.response?.data?.message || err.message }],
      });
    } finally {
      setImportLoading(false);
    }
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setForm({
      name: client.name || "",
      phone: client.phone || "",
      email: client.email || "",
      notes: client.notes || "",
      tags: Array.isArray(client.tags) ? client.tags.join(", ") : (client.tags || ""),
      company: client.company || "",
      role: client.role || "",
    });
    setAddError(null);
    setShowAdd(true);
  };

  const openDuplicate = (client) => {
    setEditingClient(null);
    setForm({
      name: client.name || "",
      phone: "",
      email: client.email || "",
      notes: client.notes || "",
      tags: Array.isArray(client.tags) ? client.tags.join(", ") : (client.tags || ""),
      company: client.company || "",
      role: client.role || "",
    });
    setAddError(null);
    setShowAdd(true);
  };

  const closeAddOrEditDialog = () => {
    setShowAdd(false);
    setEditingClient(null);
    setAddError(null);
    setForm({ name: "", phone: "", email: "", notes: "", tags: "", company: "", role: "" });
  };

  const handleAddOrEdit = async (e) => {
    e.preventDefault();
    setAddError(null);
    if (!form.name?.trim() || !form.phone?.trim()) {
      setAddError(isRTL ? "נא למלא שם ומספר טלפון." : "Please enter name and phone.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.replace(/[\s-]/g, ""),
        email: form.email?.trim() || "",
        notes: form.notes?.trim() || "",
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        company: form.company?.trim() || "",
        role: form.role?.trim() || "",
      };
      if (editingClient) {
        await updateClient(editingClient._id, payload);
      } else {
        await createClient(payload);
      }
      closeAddOrEditDialog();
      await loadClients();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      setAddError(getValidationMessage(msg, isRTL));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isRTL ? "למחוק את הלקוח?" : "Delete this client?"))
      return;
    try {
      await deleteClient(id);
      await loadClients();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const goToContracts = (phone) => {
    navigate(`/company/contracts?search=${encodeURIComponent(phone)}`);
  };

  const exportToCsv = (onlySelected = false) => {
    const list = onlySelected && selectedIds.size > 0
      ? clients.filter((c) => selectedIds.has(c._id))
      : clients;
    const headers = isRTL
      ? ["שם", "טלפון", "אימייל", "תגיות", "נוצר"]
      : ["Name", "Phone", "Email", "Tags", "Created"];
    const rows = list.map((c) => [
      escapeCsv(c.name),
      escapeCsv(c.phone),
      escapeCsv(c.email),
      escapeCsv(Array.isArray(c.tags) ? c.tags.join("; ") : c.tags),
      escapeCsv(formatDate(c.createdAt)),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const withEmailCount = clients.filter((c) => c.email && c.email.trim()).length;

  return (
    <Box sx={{ pt: 2 }}>
      {/* Row 1: Page title + Create & Export buttons */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          {isRTL ? "ניהול לקוחות" : "Clients"}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Box
            component="span"
            onMouseEnter={handleImportPopperOpen}
            onMouseLeave={handleImportPopperClose}
            sx={{ display: "inline-flex" }}
          >
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              endIcon={
                <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 18, height: 18 } }}>
                  <UploadFileIcon />
                </Box>
              }
            >
              {isRTL ? "ייבא לקוחות" : "Import clients"}
            </Button>
            <Popper
              open={Boolean(importPopperAnchor)}
              anchorEl={importPopperAnchor}
              placement={isRTL ? "bottom-end" : "bottom-start"}
              modifiers={[
                { name: "offset", options: { offset: [0, 8] } },
              ]}
              onMouseEnter={cancelImportPopperClose}
              onMouseLeave={handleImportPopperClose}
              sx={{ zIndex: 1300 }}
            >
              <Paper
                elevation={3}
                sx={{
                  px: 1.5,
                  py: 1,
                  maxWidth: 260,
                  "&:focus": { outline: "none" },
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", textAlign: "center", fontSize: "12px" }}>
                  {isRTL
                    ? "ניתן לטעון רק קבצים מסוג CSV, XLSX או XLS."
                    : "Only CSV, XLSX or XLS files can be loaded."}
                </Typography>
              </Paper>
            </Popper>
          </Box>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => exportToCsv(false)}
            disabled={clients.length === 0}
            sx={{ color: "#fff" }}
            endIcon={
              <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 18, height: 18 } }}>
                <DownloadIcon />
              </Box>
            }
          >
            {isRTL ? "ייצוא ל-CSV" : "Export CSV"}
          </Button>
          {selectedIds.size > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              onClick={() => exportToCsv(true)}
              endIcon={
                <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 18, height: 18 } }}>
                  <DownloadIcon />
                </Box>
              }
            >
              {isRTL ? `ייצוא נבחרים (${selectedIds.size})` : `Export selected (${selectedIds.size})`}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setEditingClient(null);
              setForm({ name: "", phone: "", email: "", notes: "", tags: "", company: "", role: "" });
              setAddError(null);
              setShowAdd(true);
            }}
            endIcon={
              <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 20, height: 20 } }}>
                <PlusCircleIcon />
              </Box>
            }
          >
            {isRTL ? "הוסף לקוח" : "Add Client"}
          </Button>
        </Stack>
      </Stack>

      {/* Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {isRTL ? `סה״כ ${total} לקוחות` : `Total ${total} clients`}
        {clients.length > 0 && (
          <span> · {isRTL ? `${withEmailCount} עם אימייל בעמוד זה` : `${withEmailCount} with email on this page`}</span>
        )}
      </Typography>

      {/* Row 2: Filters & search */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} gap={1}>
        <TextField
          size="small"
          placeholder={isRTL ? "חיפוש שם, טלפון, אימייל..." : "Search name, phone, email..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{isRTL ? "אימייל" : "Email"}</InputLabel>
          <Select
            value={hasEmailFilter}
            label={isRTL ? "אימייל" : "Email"}
            onChange={(e) => { setHasEmailFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">{isRTL ? "הכל" : "All"}</MenuItem>
            <MenuItem value="yes">{isRTL ? "עם אימייל" : "With email"}</MenuItem>
            <MenuItem value="no">{isRTL ? "בלי אימייל" : "No email"}</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label={isRTL ? "נוצר מ" : "Created from"} type="date" value={createdAtFrom} onChange={(e) => { setCreatedAtFrom(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <TextField size="small" label={isRTL ? "נוצר עד" : "Created to"} type="date" value={createdAtTo} onChange={(e) => { setCreatedAtTo(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <TextField size="small" label={isRTL ? "עודכן מ" : "Updated from"} type="date" value={updatedAtFrom} onChange={(e) => { setUpdatedAtFrom(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <TextField size="small" label={isRTL ? "עודכן עד" : "Updated to"} type="date" value={updatedAtTo} onChange={(e) => { setUpdatedAtTo(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        {(search || hasEmailFilter || createdAtFrom || createdAtTo || updatedAtFrom || updatedAtTo) && (
          <Button size="small" variant="outlined" onClick={clearFilters}>
            {isRTL ? "נקה סינון" : "Clear filters"}
          </Button>
        )}
      </Stack>

      {loading ? (
        <AppLoading fullPage />
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "רשימת לקוחות" : "Clients List"}
              {totalPages > 1 && ` (${isRTL ? "עמוד" : "page"} ${page} / ${totalPages})`}
            </Typography>

            {selectedIds.size > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="body2" fontWeight={500}>
                  {isRTL
                    ? `${selectedIds.size} לקוחות נבחרו`
                    : `${selectedIds.size} selected`}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: "0.75rem" }}
                >
                  {bulkLoading
                    ? "..."
                    : isRTL
                    ? `מחיקה (${selectedIds.size})`
                    : `Delete (${selectedIds.size})`}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={bulkLoading}
                  sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: "0.75rem" }}
                >
                  {isRTL ? "בטל בחירה" : "Clear"}
                </Button>
              </Stack>
            )}

            {clients.length === 0 ? (
              <Box py={6} textAlign="center">
                <Typography color="text.secondary" gutterBottom>
                  {isRTL ? "אין לקוחות" : "No clients"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isRTL
                    ? "הוסף לקוח כדי לשלוח אליו חוזים במהירות"
                    : "Add a client to send them contracts quickly"}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={clients.length > 0 && selectedIds.size === clients.length}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < clients.length}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell onClick={() => handleSort("name")} sx={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                        {isRTL ? "שם" : "Name"} {sortBy === "name" && (sortOrder === "asc" ? " ↑" : " ↓")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"} onClick={() => handleSort("phone")} sx={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                        {isRTL ? "טלפון" : "Phone"} {sortBy === "phone" && (sortOrder === "asc" ? " ↑" : " ↓")}
                      </TableCell>
                      <TableCell>{isRTL ? "אימייל" : "Email"}</TableCell>
                      <TableCell onClick={() => handleSort("createdAt")} sx={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                        {isRTL ? "נוצר" : "Created"} {sortBy === "createdAt" && (sortOrder === "asc" ? " ↑" : " ↓")}
                      </TableCell>
                      <TableCell align="center">{isRTL ? "חוזים" : "Contracts"}</TableCell>
                      <TableCell>{isRTL ? "חוזה אחרון" : "Last contract"}</TableCell>
                      <TableCell>{isRTL ? "פעולות" : "Actions"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow
                        key={client._id}
                        selected={selectedIds.has(client._id)}
                        hover
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(client._id)}
                            onChange={() => toggleSelect(client._id)}
                          />
                        </TableCell>
                        <TableCell>{client.name}</TableCell>
                        <TableCell><span dir="ltr">{client.phone}</span></TableCell>
                        <TableCell>{client.email || "-"}</TableCell>
                        <TableCell>{formatDate(client.createdAt)}</TableCell>
                        <TableCell align="center">{client.contractCount ?? 0}</TableCell>
                        <TableCell>{formatDate(client.lastContractAt)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center">
                            <Tooltip title={isRTL ? "ערוך פרטי לקוח" : "Edit client details"}>
                              <span>
                                <IconButton size="small" color="success" onClick={() => openEdit(client)} aria-label={isRTL ? "ערוך" : "Edit"} sx={{ "& svg": { width: 18, height: 18 } }}>
                                  <PencilIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={isRTL ? "שכפל ליצירת לקוח חדש" : "Duplicate to create a new client"}>
                              <span>
                                <IconButton size="small" onClick={() => openDuplicate(client)} aria-label={isRTL ? "שכפל" : "Duplicate"} sx={{ color: muiTheme.palette.text.primary, "& svg": { width: 18, height: 18 } }}>
                                  <CopyIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={isRTL ? "היסטוריית חוזים של הלקוח" : "View client contract history"}>
                              <span>
                                <IconButton size="small" color="info" onClick={() => goToContracts(client.phone)} aria-label={isRTL ? "היסטוריה" : "History"} sx={{ "& svg": { width: 18, height: 18 } }}>
                                  <ClockIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={isRTL ? "מחק את הלקוח" : "Delete client"}>
                              <span>
                                <Button size="small" color="error" variant="contained" onClick={() => handleDelete(client._id)} sx={{ minWidth: "auto", px: 1, py: 0.25, fontSize: "0.75rem" }}>{isRTL ? "מחק" : "Delete"}</Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {totalPages > 1 && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  size="small"
                />
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showAdd}
        onClose={closeAddOrEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingClient ? (isRTL ? "ערוך לקוח" : "Edit client") : (isRTL ? "הוסף לקוח" : "Add Client")}
        </DialogTitle>
        <form onSubmit={handleAddOrEdit}>
          <DialogContent>
            {addError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {addError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ pt: 0 }}>
              <TextField
                label={isRTL ? "שם" : "Name"}
                required
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder={isRTL ? "שם הלקוח" : "Client name"}
                fullWidth
                size="small"
              />
              <TextField
                label={isRTL ? "טלפון" : "Phone"}
                required
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: e.target.value }))
                }
                placeholder="0500000000"
                fullWidth
                size="small"
                inputProps={{ dir: "ltr", type: "tel", inputMode: "tel" }}
              />
              <TextField
                label={isRTL ? "אימייל" : "Email"}
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="client@example.com"
                fullWidth
                size="small"
                inputProps={{ dir: "ltr" }}
              />
              <TextField
                label={isRTL ? "חברה" : "Company"}
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                placeholder={isRTL ? "שם החברה" : "Company name"}
                fullWidth
                size="small"
              />
              <TextField
                label={isRTL ? "תפקיד" : "Role"}
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                placeholder={isRTL ? "תפקיד בארגון" : "Role in organization"}
                fullWidth
                size="small"
              />
              <TextField
                label={isRTL ? "תגיות (מופרדות בפסיק)" : "Tags (comma-separated)"}
                value={form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                placeholder="VIP, חוזר"
                fullWidth
                size="small"
              />
              <TextField
                label={isRTL ? "הערות" : "Notes"}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder={isRTL ? "הערות פנימיות" : "Internal notes"}
                fullWidth
                size="small"
                multiline
                rows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={closeAddOrEditDialog}>
              {isRTL ? "ביטול" : "Cancel"}
            </Button>
            <Button size="small" type="submit" variant="contained" disabled={saving}>
              {saving
                ? isRTL
                  ? "שומר..."
                  : "Saving..."
                : isRTL
                ? "שמור"
                : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={showImportModal}
        onClose={() => !importLoading && setShowImportModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isRTL ? "התאמת עמודות – ייבוא לקוחות" : "Column mapping – Import clients"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isRTL
              ? "התאם את עמודות הקובץ שלך לשדות במערכת (שם וטלפון חובה)."
              : "Map your file columns to system fields (name and phone required)."}
          </Typography>
          <Stack spacing={2} sx={{ pt: 0 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>{isRTL ? "שם לקוח" : "Client name"}</InputLabel>
              <Select
                value={columnMapping.name}
                label={isRTL ? "שם לקוח" : "Client name"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, name: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" required>
              <InputLabel>{isRTL ? "מספר טלפון" : "Phone"}</InputLabel>
              <Select
                value={columnMapping.phone}
                label={isRTL ? "מספר טלפון" : "Phone"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, phone: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{isRTL ? "אימייל" : "Email"}</InputLabel>
              <Select
                value={columnMapping.email}
                label={isRTL ? "אימייל" : "Email"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, email: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{isRTL ? "חברה" : "Company"}</InputLabel>
              <Select
                value={columnMapping.company}
                label={isRTL ? "חברה" : "Company"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, company: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{isRTL ? "תפקיד" : "Role"}</InputLabel>
              <Select
                value={columnMapping.role}
                label={isRTL ? "תפקיד" : "Role"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, role: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{isRTL ? "הערות" : "Notes"}</InputLabel>
              <Select
                value={columnMapping.notes}
                label={isRTL ? "הערות" : "Notes"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, notes: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{isRTL ? "תגיות" : "Tags"}</InputLabel>
              <Select
                value={columnMapping.tags}
                label={isRTL ? "תגיות" : "Tags"}
                onChange={(e) => setColumnMapping((m) => ({ ...m, tags: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {fileColumns.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {uploadedRows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {isRTL ? "תצוגה מקדימה (5 שורות ראשונות)" : "Preview (first 5 rows)"}
              </Typography>
              <TableContainer sx={{ maxHeight: 160, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{isRTL ? "שם" : "Name"}</TableCell>
                      <TableCell>{isRTL ? "טלפון" : "Phone"}</TableCell>
                      <TableCell>{isRTL ? "אימייל" : "Email"}</TableCell>
                      <TableCell>{isRTL ? "תגיות" : "Tags"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getMappedRows()
                      .slice(0, 5)
                      .map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.name || "—"}</TableCell>
                          <TableCell dir="ltr">{row.phone || "—"}</TableCell>
                          <TableCell>{row.email || "—"}</TableCell>
                          <TableCell>{Array.isArray(row.tags) && row.tags.length ? row.tags.join(", ") : "—"}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {isRTL ? `סה״כ ${uploadedRows.length} שורות בקובץ` : `Total ${uploadedRows.length} rows in file`}
              </Typography>
            </Box>
          )}
          {importResult != null && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2">
                {isRTL
                  ? `יובאו: ${importResult.imported}, נכשלו: ${importResult.failed}`
                  : `Imported: ${importResult.imported}, Failed: ${importResult.failed}`}
                {importResult.skippedDuplicates != null && importResult.skippedDuplicates > 0 && (
                  <>
                    {" · "}
                    {isRTL
                      ? `דולגו (טלפון כפול): ${importResult.skippedDuplicates}`
                      : `Skipped (duplicate phone): ${importResult.skippedDuplicates}`}
                  </>
                )}
              </Typography>
              {importResult.errors?.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} color="error.main" component="div" sx={{ mb: 0.5 }}>
                    {isRTL ? "פירוט השגיאות:" : "Error details:"}
                  </Typography>
                  <Box
                    component="div"
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      overflowX: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                      bgcolor: "background.paper",
                    }}
                  >
                    {importResult.errors.map((e, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        color="error.main"
                        component="div"
                        sx={{ py: 0.25, "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }}
                      >
                        {e.row ? (isRTL ? `שורה ${e.row}: ` : `Row ${e.row}: `) : ""}
                        {getValidationMessage(e.message, isRTL)}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => !importLoading && setShowImportModal(false)} disabled={importLoading}>
            {importResult?.imported > 0 && importResult?.failed === 0 ? (isRTL ? "סגור" : "Close") : isRTL ? "ביטול" : "Cancel"}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleImportSubmit}
            disabled={!canImport() || importLoading}
          >
            {importLoading ? (isRTL ? "מייבא..." : "Importing...") : isRTL ? "ייבא" : "Import"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyClients;
